import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@shared/ui/select";
import { Check, ChevronDown, Plus, Trash } from "lucide-react";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useAuditUsers,
  useMeterCycleAuditUserMapping,
  usePostMeterCycleAuditUserMapping,
  usePutMeterCycleAuditUserMapping,
} from "../../hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { logEvent } from "@shared/analytics/analytics";

interface ValidatorSetupDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ValidatorSetupDialog = ({
  open,
  onClose,
}: ValidatorSetupDialogProps) => {
  const remoteUtilityId = useRemoteUtilityId();
  const [search, setSearchTerm] = useState("");

  const { data: auditUsers, isLoading } = useMeterCycleAuditUserMapping(
    remoteUtilityId,
    search,
    open
  );
  console.log("debug auditUsers", auditUsers);
  const { data: validators } = useAuditUsers(remoteUtilityId, 0, open);
  const { data: supervisors } = useAuditUsers(remoteUtilityId, 2, open);
  const postMeterCycleAuditUserMappingMutation =
    usePostMeterCycleAuditUserMapping();

  const putMeterCycleAuditUserMappingMutation =
    usePutMeterCycleAuditUserMapping();

  const validatorList = mapToLabelValue(validators, "name", "id");
  const supervisorList = mapToLabelValue(supervisors, "name", "id");

  // Get all unique validators from all cycles for the dropdown
  const getAllValidators = () => {
    if (!auditUsers) return [];
    const allValidators = [];
    auditUsers.forEach((cycle) => {
      cycle.validators.forEach((validator) => {
        if (!allValidators.find((v) => v.id === validator.id)) {
          allValidators.push(validator);
        }
      });
      cycle.supervisors.forEach((supervisor) => {
        if (!allValidators.find((v) => v.id === supervisor.id)) {
          allValidators.push(supervisor);
        }
      });
    });
    return allValidators;
  };

  const allValidators = getAllValidators();

  const toggleValidator = (readCycleId: string, validatorId: string) => {
    const payload = {
      meter_cycle: readCycleId,
      audit_user: validatorId,
      remote_utility_id: remoteUtilityId,
    };
    postMeterCycleAuditUserMappingMutation.mutate(
      payload,
      {
        onSuccess: () => {
          logEvent("MX Validator Added", payload);
        },
      }
    );
    // Implementation for adding/removing validators
  };

  const deleteValidator = (readCycleId: string, validatorId: string) => {
    console.log("debug validator", readCycleId, validatorId);
    const payload = {
       meter_cycle: readCycleId,
      audit_user: validatorId,
      remote_utility_id: remoteUtilityId,
    }
    putMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
          logEvent("MX Validator Removed", payload);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] lg:max-w-[800px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validator Setup</DialogTitle>
          <DialogDescription>
            Assign validators and supervisors to read cycles.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6">
          <Label htmlFor="search">Search Cycle</Label>
          <Input
            id="search"
            placeholder="Search by name..."
            className="mt-1"
            value={search}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="mt-6 space-y-8">
          {auditUsers?.map((readCycle) => {
            return (
              <div key={readCycle.id} className="border rounded-md p-4">
                <h3 className="font-medium text-md">
                  {readCycle.meterCycleName}
                </h3>

                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">Validators</h4>
                  <div className="flex flex-wrap gap-2">
                    {readCycle.validators.map((validator) => (
                      <div
                        key={validator.id}
                        className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center"
                      >
                        {validator.name}
                        <button
                          className="ml-1 text-blue-700 hover:text-blue-900"
                          onClick={() =>
                            deleteValidator(
                              readCycle.id.toString(),
                              validator.id.toString()
                            )
                          }
                        >
                          <Trash className="h-3 w-3" />
                        </button>
                      </div>
                    ))}

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-6">
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Validator
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-60 p-2">
                        <Select
                          onValueChange={(selectedValue) =>
                            toggleValidator(
                              readCycle.id.toString(),
                              selectedValue
                            )
                          }
                        >
                          <SelectTrigger className="w-full h-9">
                            <SelectValue placeholder="Select Validator" />
                          </SelectTrigger>
                          <SelectContent>
                            {validatorList.map((validator) => (
                              <SelectItem
                                key={validator.value}
                                value={validator.value.toString()}
                              >
                                {validator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">Supervisors</h4>
                  <div className="flex flex-wrap gap-2">
                    {readCycle.supervisors.map((supervisor) => (
                      <div
                        key={supervisor.id}
                        className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center"
                      >
                        {supervisor.name}
                        <button
                          className="ml-1 text-green-700 hover:text-green-900"
                          onClick={() =>
                            deleteValidator(
                              readCycle.id.toString(),
                              supervisor.id.toString()
                            )
                          }
                        >
                          <Trash className="h-3 w-3" />
                        </button>
                      </div>
                    ))}

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-6">
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Supervisor
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-60 p-2">
                        <Select
                          onValueChange={(selectedValue) =>
                            toggleValidator(
                              readCycle.id.toString(),
                              selectedValue
                            )
                          }
                        >
                          <SelectTrigger className="w-full h-9">
                            <SelectValue placeholder="Select Supervisor" />
                          </SelectTrigger>
                          <SelectContent>
                            {supervisorList.map((supervisor) => (
                              <SelectItem
                                key={supervisor.value}
                                value={supervisor.value.toString()}
                              >
                                {supervisor.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="mr-2">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
